import { useState, ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from '@/components/icons/lucide';

import { Modal } from '@/components/modal';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/checkbox';
import { DialogTitle, VisuallyHidden } from '@/components/dialog';

interface FeedbackBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { text: string; isAnonymous: boolean }) => Promise<void>;
  placeholder: string;
  submitButtonText: string;
  additionalInfo?: ReactNode;
  title?: string;
}

export const FeedbackBaseModal = ({
  isOpen,
  onClose,
  onSubmit,
  placeholder,
  submitButtonText,
  additionalInfo,
  title = 'Feedback Form',
}: FeedbackBaseModalProps) => {
  const [text, setText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  const submitMutation = useMutation({
    mutationFn: async () => {
      await onSubmit({ text, isAnonymous });
      setText('');
      setIsAnonymous(false);
    },
  });

  return (
    <Modal showModal={isOpen} setShowModal={onClose} hideCloseButton>
      <div className="w-full p-3">
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className="w-full text-sm min-h-[120px] p-2.5 rounded-md bg-background-400/50 border-0 text-subtle-400 focus:outline-none focus:ring-1 focus:ring-background-200 resize-none mb-2"
        />

        {additionalInfo}

        <label className="flex items-center gap-2 cursor-pointer select-none mb-3">
          <Checkbox
            checked={isAnonymous}
            onCheckedChange={(checked) => setIsAnonymous(checked as boolean)}
          />
          <span className="text-sm text-subtle-500">Submit anonymously</span>
        </label>

        <div className="space-y-1.5 mt-6">
          <Button
            variant="secondary"
            className="w-full justify-center bg-background-400/50 hover:bg-background-400/70 text-subtle-400"
            disabled={!text.trim() || submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            {submitMutation.isPending ? (
              <div className="flex items-center justify-center text-sm">
                <Loader2 className="mr-2 size-4 animate-spin" />
                Sending...
              </div>
            ) : (
              <div className="flex items-center justify-center text-sm">
                {submitButtonText}
              </div>
            )}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-center text-subtle-700 hover:text-subtle-400 hover:bg-background-400/50"
            onClick={onClose}
          >
            Maybe later
          </Button>
        </div>
      </div>
    </Modal>
  );
};
